import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Bell, 
  Search, 
  Filter,
  Clock,
  Mail,
  Smartphone,
  Loader2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { FaTelegram } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AlertsPage() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const limit = 10;

  useEffect(() => {
    fetchAlerts();
  }, [page]);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/alerts`, {
        params: { skip: page * limit, limit },
        withCredentials: true
      });
      setAlerts(response.data.alerts || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  const handleSendTestAlert = async () => {
    setSendingTest(true);
    try {
      await axios.post(`${API}/alerts/test`, {}, { withCredentials: true });
      toast.success('Test alert sent successfully!');
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to send test alert');
    } finally {
      setSendingTest(false);
    }
  };

  const filteredAlerts = search 
    ? alerts.filter(a => 
        a.message_text?.toLowerCase().includes(search.toLowerCase()) ||
        a.channel_title?.toLowerCase().includes(search.toLowerCase()) ||
        a.matched_keywords?.some(k => k.toLowerCase().includes(search.toLowerCase()))
      )
    : alerts;

  const totalPages = Math.ceil(total / limit);

  const getSentViaIcon = (sentVia) => {
    switch (sentVia) {
      case 'email':
        return <Mail className="w-3 h-3" />;
      case 'sms':
        return <Smartphone className="w-3 h-3" />;
      case 'both':
        return (
          <div className="flex gap-1">
            <Mail className="w-3 h-3" />
            <Smartphone className="w-3 h-3" />
          </div>
        );
      default:
        return null;
    }
  };

  if (loading && page === 0) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        >
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-['Outfit'] tracking-tight" data-testid="alerts-page-title">
              Alert History
            </h1>
            <p className="text-muted-foreground mt-1">
              View all alerts from your monitored channels
            </p>
          </div>
          <Button 
            onClick={handleSendTestAlert}
            disabled={sendingTest}
            variant="outline"
            className="rounded-full gap-2"
            data-testid="send-test-alert"
          >
            {sendingTest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
            Send Test Alert
          </Button>
        </motion.div>

        {/* Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search alerts by channel, keyword, or message..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="alerts-search"
            />
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex items-center gap-4 text-sm text-muted-foreground"
        >
          <span>{total} total alerts</span>
          <span>•</span>
          <span>Page {page + 1} of {Math.max(1, totalPages)}</span>
        </motion.div>

        {/* Alerts List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="border-border/50">
            <CardContent className="p-0">
              {filteredAlerts.length > 0 ? (
                <div className="divide-y divide-border/50">
                  {filteredAlerts.map((alert, index) => (
                    <motion.div
                      key={alert.alert_id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.05 }}
                      className="p-4 hover:bg-muted/30 transition-colors"
                      data-testid={`alert-${alert.alert_id}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <FaTelegram className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="font-medium text-sm">{alert.channel_title}</span>
                            {alert.matched_keywords?.length > 0 && (
                              <div className="flex gap-1 flex-wrap">
                                {alert.matched_keywords.map((kw) => (
                                  <Badge key={kw} variant="secondary" className="text-xs">
                                    <Filter className="w-2 h-2 mr-1" />
                                    {kw}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                          <p className="text-sm text-foreground/90 leading-relaxed">
                            {alert.message_text}
                          </p>
                          <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(alert.created_at).toLocaleString()}
                            </span>
                            {alert.sent_via && alert.sent_via !== 'none' && (
                              <span className="flex items-center gap-1 text-success">
                                {getSentViaIcon(alert.sent_via)}
                                Sent via {alert.sent_via}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="py-16 text-center">
                  <Bell className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">
                    {search ? 'No matching alerts' : 'No alerts yet'}
                  </h3>
                  <p className="text-muted-foreground">
                    {search 
                      ? 'Try adjusting your search terms'
                      : 'Alerts will appear here when monitored channels post matching content'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Pagination */}
        {totalPages > 1 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center justify-center gap-2"
          >
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0 || loading}
              className="rounded-full"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </Button>
            <span className="text-sm text-muted-foreground px-4">
              Page {page + 1} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1 || loading}
              className="rounded-full"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </Button>
          </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
}
